import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { shippingService } from "../services/dataService";
import { Firm, ShipmentDetail, ShipmentMode, ShipmentUpsert, ShippableOrder } from "../types";
import { todayLocal } from "../utils/dates";
import { normalizePlate, parseQty, plateValid, postalValid, qtyText, shDriverNames, shErr, shFmtDate, shFmtQty, shLayerBlocked, shOrderNo, shTransportError } from "../utils/shipping";
import { usePermissions } from "../context/PermissionContext";
import ConfirmModal from "./ConfirmModal";
import { FirmPickerField, firmHue, firmInitials } from "./FirmPicker";
import OrderPickerModal from "./OrderPicker";
import { scanFeedback, useQrScanner } from "./QrScanner";
import "../pages/PurchaseOrders.css";
import "../pages/Shipping.css";

/**
 * "Bütün sipariş" sevkiyatı (siparişe üretim) — yeni taslak ve taslak düzenleme. Mobil öncelikli (depoda telefonla okutulur).
 * Satırlar SUNUCUDA tutulur: her okutma POST /Shipping/scan ile taslağa yazılır (ilk okutma taslağı açar, firma ilk
 * siparişten gelir ve kilitlenir), çıkarma DELETE ile anında gider. Yalnız taşıma / teslimat / not alanları yerel
 * formdadır ve "Taslak kaydet" ya da "Sevk et" ile gönderilir. Miktar girilmez: sipariş bölünmez, tamamı gider.
 * El okuyucuları (USB/Bluetooth) adresi yazıp Enter'a basar → okutma alanı açılışta ve her okutmadan sonra odaktadır.
 */
export interface WholeShipmentModalProps {
  /** Düzenlenen "bütün sipariş" taslağı; null = yeni. */
  shipment: ShipmentDetail | null;
  /** Derin bağlantıdan gelen sipariş kimlikleri: açılışta sırayla okutulur. */
  initialCodes?: string[];
  firms: Firm[];
  escDisabled?: boolean;
  /** e-İrsaliye açık: irsaliye numarasını sevkte sistem verir; plaka, şoför/taşıyıcı ve posta kodu sevkte zorunlu. */
  eDespatch?: boolean;
  /** Hiçbir şey değişmediyse. */
  onClose: () => void;
  /** Taslak açıldı / değişti (thenShip: sayfa sevk penceresini açar). */
  onSaved: (r: { shipmentId: string; number?: string; thenShip: boolean }) => void;
  onDeleted: (number: string) => void;
}

interface Head {
  shipmentDate: string; carrier: string; vehiclePlate: string; driverFirstName: string; driverLastName: string; driverTckn: string; trackingNo: string;
  trailerPlate: string; plateIsForeign: "" | "1"; carrierTaxNumber: string;
  packageCount: string; grossWeightKg: string; deliveryTitle: string; deliveryAddress: string; deliveryDistrict: string;
  deliveryCity: string; deliveryPostalCode: string; deliveryContact: string; deliveryPhone: string; waybillNo: string; waybillDate: string; note: string;
}
type Toast = { id: number; kind: "ok" | "dup" | "err"; text: string; sub?: string; undo?: string };

const emptyHead = (): Head => ({
  shipmentDate: todayLocal(), carrier: "", vehiclePlate: "", driverFirstName: "", driverLastName: "", driverTckn: "", trackingNo: "",
  trailerPlate: "", plateIsForeign: "", carrierTaxNumber: "", packageCount: "",
  grossWeightKg: "", deliveryTitle: "", deliveryAddress: "", deliveryDistrict: "", deliveryCity: "", deliveryPostalCode: "", deliveryContact: "",
  deliveryPhone: "", waybillNo: "", waybillDate: "", note: "",
});
const headOf = (d: ShipmentDetail): Head => {
  const [first, last] = shDriverNames(d);
  return {
    shipmentDate: d.shipmentDate.slice(0, 10), carrier: d.carrier || "", vehiclePlate: d.vehiclePlate || "",
    driverFirstName: first, driverLastName: last, driverTckn: d.driverTckn || "", trackingNo: d.trackingNo || "",
    trailerPlate: d.trailerPlate || "", plateIsForeign: d.plateIsForeign ? "1" : "", carrierTaxNumber: d.carrierTaxNumber || "",
    packageCount: d.packageCount != null ? String(d.packageCount) : "", grossWeightKg: d.grossWeightKg != null ? qtyText(d.grossWeightKg) : "",
    deliveryTitle: d.deliveryTitle || "", deliveryAddress: d.deliveryAddress || "", deliveryDistrict: d.deliveryDistrict || "",
    deliveryCity: d.deliveryCity || "", deliveryPostalCode: d.deliveryPostalCode || "", deliveryContact: d.deliveryContact || "", deliveryPhone: d.deliveryPhone || "",
    waybillNo: d.waybillNo || "", waybillDate: d.waybillDate ? d.waybillDate.slice(0, 10) : "", note: d.note || "",
  };
};
const same = (a: Head, b: Head) => JSON.stringify(a) === JSON.stringify(b);
const nz = (s: string) => s.trim() || null;
/** Karekod adresindeki sipariş kimliği (yerel tekrar denetimi için; çözüm her zaman sunucuda). */
const guidOf = (code: string): string | null => {
  const m = /\/orderdetail\/([0-9a-f-]{36})/i.exec(code) || /^\s*#?([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\s*$/i.exec(code);
  return m ? m[1].toLowerCase() : null;
};
const coarse = () => typeof window !== "undefined" && !!window.matchMedia?.("(pointer: coarse)").matches;

const I = {
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  send: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2 11 13M22 2l-7 20-4-9-9-4z" /></svg>,
  camera: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 8h3l2-3h8l2 3h3v12H3z" /><circle cx="12" cy="13.5" r="3.5" /></svg>,
  qr: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h2v2h-2zM18 14h2M14 18h2v2M18 18h2v2" /></svg>,
  list: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" /></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></svg>,
  lock: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>,
  chevron: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 5 5L20 7" /></svg>,
  warn: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3 2 20h20z" /><path d="M12 10v4M12 17.5v.5" /></svg>,
  box: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7.5 12 3l9 4.5v9L12 21l-9-4.5z" /><path d="M3 7.5 12 12l9-4.5M12 12v9" /></svg>,
};

const Field: React.FC<{ label: string; hint?: string; span?: 2; as?: "label" | "div"; children: React.ReactNode }> = ({ label, hint, span, as = "label", children }) => {
  const Tag = as as any;
  return <Tag className={`po-field ${span ? `span${span}` : ""}`}><span>{label}{hint && <em>{hint}</em>}</span>{children}</Tag>;
};

let toastSeq = 0;

const WholeShipmentModal: React.FC<WholeShipmentModalProps> = ({ shipment, initialCodes, firms, escDisabled, eDespatch, onClose, onSaved, onDeleted }) => {
  const { has, hasModule } = usePermissions();
  const canShip = has("shipping:update");
  const canDelete = has("shipping:delete");
  // Yeni taslağı ilk okutma açar (shipping:create); var olan taslağa ekleme shipping:update ister
  const canScan = shipment ? has("shipping:update") : has("shipping:create") || has("shipping:update");

  // ---- Sunucu durumu (satırlar) + yerel başlık ----
  const [detail, setDetailState] = useState<ShipmentDetail | null>(shipment);
  const detailRef = useRef<ShipmentDetail | null>(shipment);
  const setDetail = (d: ShipmentDetail | null) => { detailRef.current = d; setDetailState(d); };
  const [head, setHead] = useState<Head>(() => (shipment ? headOf(shipment) : emptyHead()));
  const [baseHead, setBaseHead] = useState<Head>(() => (shipment ? headOf(shipment) : emptyHead()));
  const baseRef = useRef(baseHead);
  baseRef.current = baseHead;
  const dirty = !same(head, baseHead);
  const changedRef = useRef(false); // okutma / çıkarma / kayıt oldu → kapanışta liste yenilensin

  const [code, setCode] = useState("");
  const [busyScan, setBusyScan] = useState(0); // kuyrukta bekleyen okutma
  const [saving, setSaving] = useState<null | "draft" | "ship" | "delete">(null);
  const [error, setError] = useState("");
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);
  const [fresh, setFresh] = useState<string | null>(null); // son eklenen satır (vurgu)
  const [cameraOn, setCameraOn] = useState(false);
  const [transportOpen, setTransportOpen] = useState(() => !!shipment && !!(shipment.carrier || shipment.vehiclePlate || shipment.waybillNo));
  const [listFirm, setListFirm] = useState<string>(shipment?.firmId ?? "");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [cands, setCands] = useState<ShippableOrder[]>([]);
  const [candLoading, setCandLoading] = useState(false);
  const [candError, setCandError] = useState("");
  const [guardOpen, setGuardOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);

  const overlayRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const queue = useRef<{ code: string; source: "input" | "camera" | "list" }[]>([]);
  const running = useRef(false);
  const toastTimer = useRef<number | undefined>(undefined);
  const recent = useRef(new Map<string, number>()); // kamera: listede olan siparişin tekrar bildirimi seyrek olsun
  const mounted = useRef(true);
  // StrictMode geliştirmede efekti sök-tak yapar: bayrak gövdede yeniden true olmalı, yoksa kuyruk hiç çalışmaz
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; window.clearTimeout(toastTimer.current); }; }, []);

  const lines = useMemo(() => detail?.lines ?? [], [detail]);
  const shown = useMemo(() => [...lines].reverse(), [lines]); // en son eklenen üstte
  const lineIds = useMemo(() => new Set(lines.map((l) => l.orderId.toLowerCase())), [lines]);
  const firmLocked = lines.length > 0;
  const firmId = detail?.firmId || listFirm;
  const firm = useMemo(() => firms.find((f) => f.firmId === firmId), [firms, firmId]);
  const firmName = detail?.firmName || firm?.firmName || "";
  const totals = useMemo(() => {
    const m = new Map<string, number>();
    lines.forEach((l) => m.set(l.unit, (m.get(l.unit) ?? 0) + l.quantity));
    return Array.from(m.entries()).map(([u, q]) => `${shFmtQty(q)} ${u}`);
  }, [lines]);

  // Açan öğe ilk render'da yakalanır; kapanışta odak ona döner
  const [opener] = useState(() => document.activeElement as HTMLElement | null);
  useEffect(() => () => { if (opener && opener.isConnected) opener.focus?.({ preventScroll: true }); }, [opener]);
  const focusInput = useCallback(() => {
    window.setTimeout(() => { if (!shLayerBlocked(overlayRef.current)) inputRef.current?.focus(); }, 0);
  }, []);
  useEffect(() => { focusInput(); }, [focusInput]);

  // Kaydedilmemiş başlık varken sekme kapatma uyarısı
  useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const say = (t: Omit<Toast, "id">) => {
    window.clearTimeout(toastTimer.current);
    setToast({ ...t, id: ++toastSeq });
    toastTimer.current = window.setTimeout(() => { if (mounted.current) setToast(null); }, t.kind === "err" ? 6000 : t.undo ? 6000 : 3500);
  };

  /** Sunucudan gelen taslağı benimse: yeni açıldıysa ve kullanıcı başlığa dokunmadıysa sunucunun başlığı (firma adresi) alınır. */
  const adopt = (d: ShipmentDetail) => {
    const h = headOf(d);
    const wasNew = !detailRef.current;
    setDetail(d);
    setListFirm(d.firmId);
    if (wasNew) {
      setHead((cur) => (same(cur, baseRef.current) ? h : cur));
      setBaseHead(h);
    }
  };

  // ---- Okutma kuyruğu: sırayla, her biri sunucuda doğrulanır ----
  const drain = async () => {
    if (running.current) return;
    running.current = true;
    while (queue.current.length > 0 && mounted.current) {
      const job = queue.current.shift()!;
      setBusyScan(queue.current.length + 1);
      try {
        const r = await shippingService.scan(job.code, detailRef.current?.shipmentId);
        if (!mounted.current) break;
        changedRef.current = true;
        adopt(r.shipment);
        const o = r.order;
        const what = [o.modelCode, `${shFmtQty(o.quantity)} ${o.unit}`].filter(Boolean).join(" · ");
        recent.current.set(o.orderId.toLowerCase(), Date.now());
        if (r.alreadyAdded) { scanFeedback("dup"); say({ kind: "dup", text: `${o.orderNo} zaten listede`, sub: what }); }
        else {
          scanFeedback("ok");
          setFresh(o.orderId.toLowerCase());
          say({ kind: "ok", text: r.created ? `${o.orderNo} eklendi · taslak ${r.shipment.number} açıldı` : `${o.orderNo} eklendi`, sub: what });
        }
        setError("");
      } catch (e) {
        if (!mounted.current) break;
        scanFeedback("err");
        say({ kind: "err", text: shErr(e, "Sipariş eklenemedi."), sub: job.code.length > 60 ? job.code.slice(0, 60) + "…" : job.code });
      }
    }
    running.current = false;
    if (mounted.current) {
      setBusyScan(0);
      // Masaüstünde (el okuyucusu) odak okutma alanına döner; dokunmatikte klavye açılıp kamerayı örtmesin
      if (!coarse()) focusInput();
    }
  };
  const enqueue = (c: string, source: "input" | "camera" | "list") => {
    const t = c.trim();
    if (!t) return;
    queue.current.push({ code: t, source });
    setBusyScan(queue.current.length);
    drain();
  };

  // Derin bağlantı: açılışta sırayla okut
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current || !initialCodes?.length || !canScan) return;
    seeded.current = true;
    Array.from(new Set(initialCodes)).forEach((c) => enqueue(c, "list"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitCode = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!canScan) return;
    const c = code.trim();
    if (!c) { focusInput(); return; }
    setCode("");
    enqueue(c, "input");
    focusInput();
  };

  // ---- Kamera (ortak jsQR okuyucu, arka kamera, aynı kod 2 sn'de bir) ----
  const { videoRef, canvasRef, camera } = useQrScanner({
    enabled: cameraOn, facingMode: "environment", width: 1280, height: 720, dedupeMs: 2000,
    onCode: (d) => {
      const g = guidOf(d);
      if (g && lineIds.has(g)) {
        // Listedeki siparişin karekodu kadrajda durdukça her 2 sn'de uyarı çalmasın: 6 sn'de bir hatırlat
        const last = recent.current.get(g) ?? 0;
        if (Date.now() - last > 6000) { recent.current.set(g, Date.now()); scanFeedback("dup"); say({ kind: "dup", text: `${shOrderNo(g)} zaten listede` }); }
        return;
      }
      enqueue(d, "camera");
    },
  });
  const secure = typeof window === "undefined" || window.isSecureContext;

  // ---- Listeden ekle (OrderPicker, yalnız uygun tamamlanmış siparişler, miktar yok) ----
  const loadCands = useCallback(async () => {
    if (!firmId) return;
    setCandLoading(true); setCandError("");
    try { setCands(await shippingService.wholeOrders(firmId)); }
    catch { setCandError("Siparişler yüklenemedi."); }
    finally { setCandLoading(false); }
  }, [firmId]);
  const openPicker = () => { if (!firmId || !canScan) return; setPickerOpen(true); loadCands(); };
  // Taslaktaki siparişler de seçicide işaretli görünsün (sunucu listesi onları içermez)
  const pickerOrders = useMemo<ShippableOrder[]>(() => [
    ...lines.map((l) => ({
      orderId: l.orderId, modelCode: l.modelCode, modelName: l.modelName, acceptanceDate: l.orderDate || "", deadline: l.deadline,
      orderStatusId: l.orderStatusId ?? 4, orderStatusName: l.orderStatusName, quantity: l.quantity, unit: l.unit,
      shipped: 0, remaining: l.quantity, invoiceNumber: null, completionDate: l.completionDate,
    })),
    ...cands.filter((c) => !lineIds.has(c.orderId.toLowerCase())),
  ], [lines, cands, lineIds]);

  const applyPicked = async (ids: string[]) => {
    const want = new Set(ids.map((x) => x.toLowerCase()));
    const toRemove = lines.filter((l) => !want.has(l.orderId.toLowerCase()));
    const toAdd = ids.filter((x) => !lineIds.has(x.toLowerCase()));
    for (const l of toRemove) await removeLine(l.orderId, false);
    toAdd.forEach((x) => enqueue(x, "list"));
  };

  // ---- Satır çıkarma (anında sunucuda; "Geri al" yeniden ekler) ----
  const removeLine = async (orderId: string, withUndo = true) => {
    const d = detailRef.current;
    if (!d) return;
    try {
      const nd = await shippingService.removeOrder(d.shipmentId, orderId);
      changedRef.current = true;
      setDetail(nd);
      if (withUndo) say({ kind: "dup", text: `${shOrderNo(orderId)} sevkiyattan çıkarıldı`, undo: orderId });
    } catch (e) { say({ kind: "err", text: shErr(e, "Sipariş çıkarılamadı.") }); }
    focusInput();
  };

  // ---- Başlık doğrulama + kayıt ----
  const set = (k: keyof Head, v: string) => { setHead((h) => ({ ...h, [k]: v })); setError(""); };
  const tErr = shTransportError(head);
  // e-İrsaliye ipuçları (kaydı engellemez; sevk penceresi zorunlu alanları ayrıca sorar)
  const foreign = head.plateIsForeign === "1";
  const plateHint = eDespatch && head.vehiclePlate.trim() && !plateValid(head.vehiclePlate, foreign) ? "e-İrsaliye bu plakayı kabul etmez: 01–81 il koduyla başlamalı (34ABC123); yabancı plakaysa işaretleyin." : "";
  const postalHint = eDespatch && /^\d{5}$/.test(head.deliveryPostalCode.trim()) && !postalValid(head.deliveryPostalCode) ? "Posta kodu 01–81 il koduyla başlamalı." : "";

  const showError = (msg: string, field?: string) => {
    setError(msg);
    if (field) {
      setTransportOpen(true);
      window.setTimeout(() => { const el = overlayRef.current?.querySelector<HTMLInputElement>(`[data-f="${field}"]`); el?.focus(); el?.scrollIntoView({ block: "center" }); }, 0);
    } else bodyRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const validate = (): boolean => {
    setTried(true);
    if (!detailRef.current || lines.length === 0) { showError("Önce en az bir sipariş okutun ya da listeden ekleyin."); return false; }
    if (!head.shipmentDate) { showError("Sevk tarihi girin.", "shipmentDate"); return false; }
    if (tErr) { showError(tErr.msg, tErr.field); return false; }
    return true;
  };

  const dto = (d: ShipmentDetail): ShipmentUpsert => {
    const gw = parseQty(head.grossWeightKg);
    return {
      mode: ShipmentMode.WholeOrder,
      firmId: d.firmId, shipmentDate: head.shipmentDate,
      // e-İrsaliye açıkken plaka GİB biçimine çevrilir (Türkçe harf → Latin); kapalıyken yazıldığı gibi
      carrier: nz(head.carrier), vehiclePlate: nz(eDespatch ? normalizePlate(head.vehiclePlate) : head.vehiclePlate),
      // Ad/soyad ayrı gider; sunucu görüntüleme adını (driverName) birleştirir
      driverFirstName: nz(head.driverFirstName), driverLastName: nz(head.driverLastName),
      driverTckn: nz(head.driverTckn), trackingNo: nz(head.trackingNo),
      trailerPlate: nz(eDespatch ? normalizePlate(head.trailerPlate) : head.trailerPlate), plateIsForeign: foreign, carrierTaxNumber: nz(head.carrierTaxNumber),
      packageCount: head.packageCount.trim() ? parseInt(head.packageCount.trim(), 10) : null,
      grossWeightKg: gw.ok ? gw.value : gw.code === "zero" ? 0 : null,
      deliveryTitle: nz(head.deliveryTitle), deliveryAddress: nz(head.deliveryAddress),
      deliveryDistrict: nz(head.deliveryDistrict), deliveryCity: nz(head.deliveryCity), deliveryPostalCode: nz(head.deliveryPostalCode),
      deliveryContact: nz(head.deliveryContact), deliveryPhone: nz(head.deliveryPhone),
      waybillNo: nz(head.waybillNo), waybillDate: head.waybillNo.trim() && head.waybillDate ? head.waybillDate : null,
      note: nz(head.note),
      // Bütün siparişte miktar sunucuda yazılır; yalnız sipariş kimlikleri (ve varsa satır kolisi) gider
      lines: d.lines.map((l) => ({ orderId: l.orderId, quantity: 0, packageCount: l.packageCount ?? null })),
    };
  };

  const saveHead = async (): Promise<ShipmentDetail | null> => {
    const d = detailRef.current!;
    if (!dirty) return d;
    await shippingService.update(d.shipmentId, dto(d));
    const nd = await shippingService.shipment(d.shipmentId);
    changedRef.current = true;
    setDetail(nd);
    const h = headOf(nd);
    setHead(h); setBaseHead(h);
    return nd;
  };

  const submit = async (thenShip: boolean) => {
    if (saving || busyScan) return;
    if (!validate()) return;
    setSaving(thenShip ? "ship" : "draft"); setError("");
    try {
      const nd = await saveHead();
      if (!nd) return;
      onSaved({ shipmentId: nd.shipmentId, number: nd.number, thenShip });
    } catch (e) { showError(shErr(e, "Sevkiyat kaydedilemedi.")); }
    finally { if (mounted.current) setSaving(null); }
  };

  const doDelete = async () => {
    const d = detailRef.current;
    setDelOpen(false);
    if (!d) return;
    setSaving("delete");
    try { await shippingService.remove(d.shipmentId); onDeleted(d.number); }
    catch (e) { showError(shErr(e, "Taslak silinemedi.")); setSaving(null); }
  };

  const finish = () => {
    const d = detailRef.current;
    if (d && changedRef.current) onSaved({ shipmentId: d.shipmentId, number: d.number, thenShip: false });
    else onClose();
  };
  const requestClose = () => { if (saving) return; if (dirty && detailRef.current) setGuardOpen(true); else finish(); };

  // ---- Kısayollar: Esc (kamera açıksa önce kamerayı kapatır) · Ctrl+S taslak kaydet · F2 listeden ekle ----
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (escDisabled || guardOpen || delOpen || pickerOpen || shLayerBlocked(overlayRef.current)) return;
      if (e.key === "Escape") {
        e.preventDefault(); e.stopPropagation();
        if (cameraOn) { setCameraOn(false); focusInput(); } else requestClose();
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") { e.preventDefault(); submit(false); }
      else if (e.key === "F2") { e.preventDefault(); openPicker(); }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  });

  const isNew = !detail;
  const today0 = new Date().setHours(0, 0, 0, 0);

  return (
    <div className="po-overlay center" ref={overlayRef} onMouseDown={(e) => { if (e.target === e.currentTarget) requestClose(); }}>
      <form className="po-modal xl sh-wo" role="dialog" aria-modal="true" aria-labelledby="sh-wo-title" onSubmit={(e) => e.preventDefault()}>
        <div className="po-modal-head">
          <div>
            <span className="po-kicker">Sevkiyat · Bütün sipariş</span>
            <h3 id="sh-wo-title">{detail ? `Taslak · ${detail.number}` : "Yeni sevkiyat · karekodla"}</h3>
            <p className="po-form-hint">Tamamlanan siparişlerin karekodunu okutun; sipariş bölünmez, tamamı tek paket olarak sevk edilir.</p>
          </div>
          <div className="po-modal-head-side">
            {dirty && detail && <span className="po-dirty">Kaydedilmemiş değişiklik</span>}
            <button type="button" className="po-ib big" aria-label="Kapat" onClick={requestClose}>{I.close}</button>
          </div>
        </div>

        <div className="po-modal-body" ref={bodyRef}>
          {error && <div className="po-note err" role="alert">{error}</div>}
          {!canScan && <div className="po-note warn">Bu taslağa sipariş eklemek için "Sevkiyat · Güncelle" yetkisi gerekir.</div>}

          {/* A — Okutma */}
          <div className="sh-wo-scan">
            <div className="sh-wo-scanrow" role="search">
              <span className="sh-wo-scanico" aria-hidden="true">{I.qr}</span>
              <input ref={inputRef} className="sh-wo-input" value={code} disabled={!canScan} autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false}
                enterKeyHint="go" aria-label="Sipariş karekodu ya da sipariş no" placeholder="Karekodu okutun ya da sipariş no yazın"
                onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") submitCode(e); }} />
              <button type="button" className="po-btn sh-wo-add" disabled={!canScan || !code.trim()} onClick={() => submitCode()}>Ekle</button>
            </div>
            <div className="sh-wo-scanbtns">
              <button type="button" className={`po-btn ${cameraOn ? "on" : ""}`} disabled={!canScan} aria-pressed={cameraOn}
                onClick={() => { setCameraOn((v) => !v); if (cameraOn) focusInput(); }}>{I.camera} {cameraOn ? "Kamerayı kapat" : "Kamerayla okut"}</button>
              <button type="button" className="po-btn" disabled={!canScan || !firmId} title={firmId ? undefined : "Önce bir sipariş okutun ya da firma seçin"} onClick={openPicker}>{I.list} Listeden ekle <kbd className="po-kbd sh-wo-kbd">F2</kbd></button>
              {busyScan > 0 && <span className="sh-wo-busy" role="status"><span className="po-spin" /> {busyScan > 1 ? `${busyScan} okutma işleniyor` : "Doğrulanıyor"}</span>}
            </div>
            {cameraOn && (
              <div className={`sh-cam ${camera}`}>
                <video ref={videoRef} playsInline muted />
                <canvas ref={canvasRef} hidden />
                {camera === "on" && <div className="sh-cam-frame" aria-hidden="true" />}
                {camera === "idle" && <div className="sh-cam-msg"><span>Kamera açılıyor…</span></div>}
                {camera === "denied" && <div className="sh-cam-msg"><b>Kamera izni verilmedi</b><span>Tarayıcı ayarlarından kameraya izin verin ya da kodu el okuyucusuyla okutun.</span></div>}
                {camera === "none" && <div className="sh-cam-msg"><b>Kamera kullanılamıyor</b><span>{secure ? "Bu cihazda kamera bulunamadı." : "Kamera yalnız güvenli bağlantıda (https) açılır."} Kodu el okuyucusuyla okutun ya da yazın.</span></div>}
                {camera === "on" && <div className="sh-cam-hint">Karekodu çerçeveye getirin · okunan her sipariş otomatik eklenir</div>}
              </div>
            )}
          </div>

          {/* B — Firma */}
          <div className="sh-wo-firm">
            {firmLocked ? (
              <>
                <span className="po-mark" style={{ ["--po-hue" as any]: firmHue(firmName) }}>{firmInitials(firmName)}</span>
                <div className="sh-wo-firmtext"><b>{firmName}</b><small>{I.lock} İlk siparişle belirlendi; başka firmanın siparişi eklenmez.</small></div>
              </>
            ) : (
              <div className="sh-wo-firmpick">
                <span className="po-fieldnote">{detail ? "Taslak boş: okutulan ilk sipariş firmayı belirler." : "Firma, okutulan ilk siparişten alınır. Listeden eklemek için firma seçebilirsiniz."}</span>
                <FirmPickerField value={listFirm} firms={firms} allowCreate={false} placeholder="Firma seçin (listeden eklemek için)" onChange={(f) => setListFirm(f?.firmId || "")} />
              </div>
            )}
          </div>

          {/* C — Siparişler */}
          <div className="po-form-sec">
            <h4>Siparişler <em>{lines.length}</em>{totals.length > 0 && <span className="sh-wo-tot">{totals.join(" · ")}</span>}</h4>
            {lines.length === 0 ? (
              <div className="po-blank small"><span className="po-blank-icon">{I.box}</span><b>Henüz sipariş yok</b>Etiketteki karekodu okutun. Yalnız durumu Tamamlandı olan ve başka bir sevkiyatta olmayan siparişler eklenir.</div>
            ) : (
              <ul className="sh-wo-list">
                {shown.map((l, i) => {
                  const no = shOrderNo(l.orderId);
                  const late = l.deadline ? Math.round((new Date(l.deadline).setHours(0, 0, 0, 0) - today0) / 86400000) : null;
                  return (
                    <li key={l.shipmentLineId} className={`sh-wo-line ${fresh === l.orderId.toLowerCase() ? "fresh" : ""}`}>
                      <span className="sh-wo-idx">{lines.length - i}</span>
                      <div className="sh-wo-main">
                        <div className="sh-wo-top"><span className="po-codepill sh-wo-no">{no}</span><b>{l.modelCode || "Model yok"}</b></div>
                        {l.modelName && <div className="po-sub">{l.modelName}</div>}
                        <div className="sh-wo-meta">
                          <span>Termin <b>{shFmtDate(l.deadline)}</b>{late !== null && late < 0 && <span className="po-badge err">{-late} gün geç</span>}</span>
                          <span>Tamamlandı <b>{shFmtDate(l.completionDate)}</b></span>
                        </div>
                      </div>
                      <div className="sh-wo-qty"><b>{shFmtQty(l.quantity)}</b><span>{l.unit}</span></div>
                      {canScan && <button type="button" className="po-ib danger sh-wo-rm" title="Sevkiyattan çıkar" aria-label={`${no} sevkiyattan çıkar`} onClick={() => removeLine(l.orderId)}>{I.close}</button>}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* D — Taşıma ve teslimat (katlanır) */}
          <div className="po-form-sec">
            <h4>
              <button type="button" className="sh-wo-toggle" aria-expanded={transportOpen} onClick={() => setTransportOpen((v) => !v)}>
                <span className={`sh-wo-chev ${transportOpen ? "open" : ""}`}>{I.chevron}</span>Taşıma ve teslimat
                {!transportOpen && <em className="sh-wo-sum">{[head.vehiclePlate, head.carrier, !eDespatch && head.waybillNo && `İrsaliye ${head.waybillNo}`].filter(Boolean).join(" · ") || (eDespatch ? "plaka, şoför, posta kodu sevkte zorunlu" : "isteğe bağlı")}</em>}
              </button>
            </h4>
            {transportOpen && (
              <>
                <div className="po-grid4">
                  <Field label="Sevk tarihi" hint="zorunlu"><input type="date" data-f="shipmentDate" value={head.shipmentDate} className={tried && !head.shipmentDate ? "is-invalid" : ""} onChange={(e) => set("shipmentDate", e.target.value)} /></Field>
                  <Field label="Nakliyeci / kargo"><input value={head.carrier} maxLength={100} onChange={(e) => set("carrier", e.target.value)} /></Field>
                  <Field label="Plaka" hint={eDespatch ? "sevkte zorunlu" : undefined}>
                    <input value={head.vehiclePlate} maxLength={20} placeholder="34ABC123" onChange={(e) => set("vehiclePlate", e.target.value.toUpperCase())} />
                    {plateHint && <span className="po-fieldnote warn">{plateHint}</span>}
                  </Field>
                  <Field label="Dorse plakası" hint="isteğe bağlı"><input value={head.trailerPlate} maxLength={20} onChange={(e) => set("trailerPlate", e.target.value.toUpperCase())} /></Field>
                  <Field label="Plaka türü" as="div">
                    <label className="sh-ed-checkrow"><input type="checkbox" checked={foreign} onChange={(e) => set("plateIsForeign", e.target.checked ? "1" : "")} /> Yabancı plakalı araç</label>
                  </Field>
                  <Field label="Şoför adı"><input value={head.driverFirstName} maxLength={50} onChange={(e) => set("driverFirstName", e.target.value)} /></Field>
                  <Field label="Şoför soyadı"><input value={head.driverLastName} maxLength={50} onChange={(e) => set("driverLastName", e.target.value)} /></Field>
                  <Field label="Şoför TCKN" hint="11 hane">
                    <input data-f="driverTckn" value={head.driverTckn} maxLength={11} inputMode="numeric" className={tried && tErr?.field === "driverTckn" ? "is-invalid" : ""} onChange={(e) => set("driverTckn", e.target.value.replace(/\D/g, ""))} />
                    {head.driverTckn.length > 0 && head.driverTckn.length < 11 && <span className="po-fielderr">11 haneli olmalı</span>}
                  </Field>
                  <Field label="Taşıyıcı VKN / TCKN" hint={eDespatch ? "şoför yoksa" : "isteğe bağlı"}>
                    <input data-f="carrierTaxNumber" value={head.carrierTaxNumber} maxLength={11} inputMode="numeric" className={tried && tErr?.field === "carrierTaxNumber" ? "is-invalid" : ""} onChange={(e) => set("carrierTaxNumber", e.target.value.replace(/\D/g, ""))} />
                  </Field>
                  <Field label="Takip no"><input value={head.trackingNo} maxLength={50} onChange={(e) => set("trackingNo", e.target.value)} /></Field>
                  <Field label="Koli sayısı" as="div">
                    <div className="po-money"><input data-f="packageCount" inputMode="numeric" placeholder="0" aria-label="Koli sayısı" value={head.packageCount} className={tried && tErr?.field === "packageCount" ? "is-invalid" : ""} onChange={(e) => set("packageCount", e.target.value)} /><i>koli</i></div>
                  </Field>
                  <Field label="Brüt ağırlık" as="div">
                    <div className="po-money"><input data-f="grossWeightKg" inputMode="decimal" placeholder="0" aria-label="Brüt ağırlık" value={head.grossWeightKg} className={tried && tErr?.field === "grossWeightKg" ? "is-invalid" : ""} onChange={(e) => set("grossWeightKg", e.target.value)} /><i>kg</i></div>
                  </Field>
                  {!eDespatch && <>
                    <Field label="Resmi irsaliye no" hint="isteğe bağlı"><input value={head.waybillNo} maxLength={32} onChange={(e) => set("waybillNo", e.target.value)} /><span className="po-fieldnote">Sevk ederken de girilebilir</span></Field>
                    <Field label="İrsaliye tarihi">
                      <input type="date" value={head.waybillDate} disabled={!head.waybillNo.trim()} onChange={(e) => set("waybillDate", e.target.value)} />
                      {!head.waybillNo.trim() && <span className="po-fieldnote">İrsaliye no girilince etkin</span>}
                    </Field>
                  </>}
                  <Field label="Teslim yeri / unvan" span={2}><input value={head.deliveryTitle} maxLength={200} placeholder={firmName || "Boş bırakılırsa firma unvanı"} onChange={(e) => set("deliveryTitle", e.target.value)} /></Field>
                  <Field label="Adres" span={2}><input value={head.deliveryAddress} maxLength={500} placeholder={firm?.address || "Boş bırakılırsa firma adresi kullanılır"} onChange={(e) => set("deliveryAddress", e.target.value)} /></Field>
                  <Field label="İlçe"><input value={head.deliveryDistrict} maxLength={100} onChange={(e) => set("deliveryDistrict", e.target.value)} /></Field>
                  <Field label="İl"><input value={head.deliveryCity} maxLength={100} onChange={(e) => set("deliveryCity", e.target.value)} /></Field>
                  <Field label="Posta kodu" hint={eDespatch ? "sevkte zorunlu" : "5 hane"}>
                    <input data-f="deliveryPostalCode" value={head.deliveryPostalCode} maxLength={5} inputMode="numeric" placeholder={firm?.postalCode || "34200"} className={tried && tErr?.field === "deliveryPostalCode" ? "is-invalid" : ""} onChange={(e) => set("deliveryPostalCode", e.target.value.replace(/\D/g, ""))} />
                    {postalHint && <span className="po-fieldnote warn">{postalHint}</span>}
                  </Field>
                  <Field label="İlgili kişi"><input value={head.deliveryContact} maxLength={100} onChange={(e) => set("deliveryContact", e.target.value)} /></Field>
                  <Field label="Telefon"><input value={head.deliveryPhone} maxLength={30} inputMode="tel" onChange={(e) => set("deliveryPhone", e.target.value)} /></Field>
                  <Field label="Not" span={2}><input value={head.note} maxLength={500} placeholder="Teslimat koşulu, alıcıya not…" onChange={(e) => set("note", e.target.value)} /></Field>
                </div>
                <p className="po-form-hint">{eDespatch
                  ? "Teslimat alanları boş bırakılırsa firma kartından kopyalanır. e-İrsaliye açık: irsaliye numarasını sevkte sistem verir; plaka, şoför (ad, soyad, TCKN) ya da taşıyıcı VKN'si ve posta kodu gerekir."
                  : "Teslimat alanları boş bırakılırsa firma kartından kopyalanır. Resmi irsaliye numarası bu siparişlerin faturasında referans olarak kullanılır."}</p>
              </>
            )}
            {isNew && dirty && <p className="po-fieldnote">Taşıma bilgileri ilk sipariş okutulup taslak açıldıktan sonra kaydedilir.</p>}
          </div>
          {!hasModule("stock") && <div className="po-note">Stok modülü lisansta olmadığı için sevk yalnız miktar takibi yapar; stok bakiyesi değişmez.</div>}
        </div>

        <div className="po-modal-foot between">
          <div className="po-sum">
            <div><span>Sipariş</span><b>{lines.length}</b></div>
            <div className="grand"><span>Toplam</span><b>{totals.join(" · ") || "—"}</b></div>
          </div>
          <div className="po-foot-btns">
            <button type="button" className="po-btn" onClick={requestClose} disabled={!!saving}>Kapat</button>
            {detail && canDelete && <button type="button" className="po-btn danger" onClick={() => setDelOpen(true)} disabled={!!saving}>{I.trash} Sil</button>}
            <button type="button" className={`po-btn ${canShip ? "" : "primary"}`} onClick={() => submit(false)} disabled={!!saving || !!busyScan || !detail || lines.length === 0}>{saving === "draft" ? "Kaydediliyor" : "Taslak kaydet"}</button>
            {canShip && <button type="button" className="po-btn primary" onClick={() => submit(true)} disabled={!!saving || !!busyScan || !detail || lines.length === 0}>{I.send} {saving === "ship" ? "Kaydediliyor" : "Sevk et"}</button>}
          </div>
        </div>

        {toast && (
          <div key={toast.id} className={`sh-toast ${toast.kind}`} role="status" aria-live="polite">
            <span className="sh-toast-ico">{toast.kind === "ok" ? I.check : toast.kind === "err" ? I.warn : I.box}</span>
            <span className="sh-toast-text"><b>{toast.text}</b>{toast.sub && <small>{toast.sub}</small>}</span>
            {toast.undo && <button type="button" className="po-link" onClick={() => { const id = toast.undo!; setToast(null); enqueue(id, "list"); }}>Geri al</button>}
          </div>
        )}
      </form>
      {/* aria-live bölgesi her zaman DOM'da: ekran okuyucu ilk bildirimi de okusun */}
      <div className="fp-sr" role="status" aria-live="polite">{toast ? `${toast.text}${toast.sub ? `, ${toast.sub}` : ""}` : ""}</div>

      <OrderPickerModal
        open={pickerOpen} whole
        onClose={() => { setPickerOpen(false); focusInput(); }}
        orders={pickerOrders} loading={candLoading} error={candError} onReload={loadCands}
        value={lines.map((l) => l.orderId)} onApply={(ids) => { applyPicked(ids); }} firmName={firmName}
        title="Tamamlanan siparişlerden ekle"
      />
      <ConfirmModal isOpen={guardOpen} type="warning" title="Taşıma bilgileri kaydedilmedi"
        message="Okutulan siparişler taslakta duruyor; yalnız taşıma ve teslimat alanlarındaki değişiklikler kaybolur."
        confirmText="Kaydetmeden kapat" cancelText="Düzenlemeye dön" onConfirm={() => { setGuardOpen(false); finish(); }} onCancel={() => setGuardOpen(false)} />
      <ConfirmModal isOpen={delOpen} type="danger" title="Taslağı sil"
        message={`${detail?.number ?? "Taslak"} kalıcı olarak silinecek. İçindeki ${lines.length} sipariş yeniden sevk edilebilir olur.`}
        confirmText="Sil" cancelText="Vazgeç" onConfirm={doDelete} onCancel={() => setDelOpen(false)} />
    </div>
  );
};

export default WholeShipmentModal;
